import React from 'react';
import { PrimeIndemnite } from '../../types';
import IFTDConfig from './prime-configs/IFTDConfig';
import ISSConfig from './prime-configs/ISSConfig';
import ContagionConfig from './prime-configs/ContagionConfig';
import PrimeNuitConfig from './prime-configs/PrimeNuitConfig';
import SFTConfig from './prime-configs/SFTConfig';
import IRZoneSelector from './prime-configs/IRZoneSelector';
import InsalubriteCat2Config from './prime-configs/InsalubriteCat2Config';
import NBIConfig from './prime-configs/NBIConfig';
import EditableAmount from './EditableAmount';

interface PrimeConfigPanelProps {
  prime: PrimeIndemnite;
  currentIndiceMajore?: number | null;
  selectedGrade?: number | null;
  selectedEchelon?: number;
  selectedZone?: number | null;
  onZoneChange?: (zone: number) => void;
  onIFTDConfigChange?: (config: { dimanchesParMois?: number; joursFeriesTravailles?: string[] }) => void;
  onISSConfigChange?: (config: { echelonHistorique?: number; indiceMajoreHistorique?: number }) => void;
  onContagionConfigChange?: (config: { joursTravaillesParAn?: number }) => void;
  onPrimeNuitConfigChange?: (config: { nuitsParMois?: number }) => void;
  onSFTConfigChange?: (config: { nombreEnfants?: number }) => void;
  onTransportBaseAmountChange?: (primeId: string, amount: number) => void;
  onInsalubriteCat2ConfigChange?: (config: { joursTravaillesParMois?: number }) => void;
  onNBIConfigChange?: (config: { points?: number }) => void;
  onAmountChange?: (amount: number) => void;
}

export default function PrimeConfigPanel({
  prime,
  currentIndiceMajore,
  selectedGrade,
  selectedEchelon,
  selectedZone,
  onZoneChange,
  onIFTDConfigChange,
  onISSConfigChange,
  onContagionConfigChange,
  onPrimeNuitConfigChange,
  onSFTConfigChange,
  onTransportBaseAmountChange,
  onInsalubriteCat2ConfigChange,
  onNBIConfigChange,
  onAmountChange
}: PrimeConfigPanelProps) {
  const Icon = prime.icon;

  // Logs pour diagnostiquer la prime d'insalubrité
  console.log('🔍 PrimeConfigPanel - Prime ID:', prime.id);
  console.log('🔍 PrimeConfigPanel - onInsalubriteCat2ConfigChange:', !!onInsalubriteCat2ConfigChange);
  console.log('🔍 PrimeConfigPanel - prime.montantDemiJourneeInsalubrite:', prime.montantDemiJourneeInsalubrite);
  console.log('🔍 PrimeConfigPanel - prime.insalubriteCat2Config:', prime.insalubriteCat2Config);

  const renderConfigComponent = () => {
    switch (prime.id) {
      case 'indemnite_residence':
        if (onZoneChange && currentIndiceMajore) {
          return (
            <IRZoneSelector
              selectedZone={selectedZone}
              onZoneChange={onZoneChange}
              currentIndiceMajore={currentIndiceMajore}
            />
          );
        }
        return null;

      case 'iss':
        console.log('🔍 PrimeConfigPanel - ISS case reached');
        console.log('🔍 PrimeConfigPanel - onISSConfigChange exists:', !!onISSConfigChange);
        console.log('🔍 PrimeConfigPanel - selectedGrade:', selectedGrade);
        console.log('🔍 PrimeConfigPanel - selectedEchelon:', selectedEchelon);
        if (onISSConfigChange && selectedGrade && selectedEchelon) {
          console.log('✅ PrimeConfigPanel - Rendering ISSConfig');
          return (
            <ISSConfig
              config={prime.issConfig || { echelonHistorique: selectedEchelon, indiceMajoreHistorique: currentIndiceMajore || 0 }}
              onChange={(config) => {
                console.log('🔧 PrimeConfigPanel - ISSConfig onChange called with:', config);
                onISSConfigChange(config);
              }}
              currentEchelon={selectedEchelon}
              currentGrade={selectedGrade}
            />
          );
        } else {
          console.log('❌ PrimeConfigPanel - ISSConfig conditions not met');
        }
        return null;

      case 'iftd':
        if (onIFTDConfigChange) {
          return (
            <IFTDConfig
              config={prime.iftdConfig || { dimanchesParMois: 0, joursFeriesTravailles: [] }}
              onChange={onIFTDConfigChange}
            />
          );
        }
        return null;

      case 'contagion':
        if (onContagionConfigChange) {
          return (
            <ContagionConfig
              config={prime.contagionConfig || { joursTravaillesParAn: 228 }}
              onChange={onContagionConfigChange}
            />
          );
        }
        return null;

      case 'prime_nuit':
        if (onPrimeNuitConfigChange && currentIndiceMajore) {
          return (
            <PrimeNuitConfig
              config={prime.primeNuitConfig || { nuitsParMois: 0 }}
              onChange={onPrimeNuitConfigChange}
              currentIndiceMajore={currentIndiceMajore}
            />
          );
        }
        return null;

      case 'sft':
        if (onSFTConfigChange && currentIndiceMajore) {
          return (
            <SFTConfig
              config={prime.sftConfig || { nombreEnfants: 0 }}
              onChange={onSFTConfigChange}
              currentIndiceMajore={currentIndiceMajore}
            />
          );
        }
        return null;

      case 'insalubrite':
        console.log('🎯 PrimeConfigPanel - insalubrite case reached');
        console.log('🎯 PrimeConfigPanel - onInsalubriteCat2ConfigChange exists:', !!onInsalubriteCat2ConfigChange);
        console.log('🎯 PrimeConfigPanel - prime.montantDemiJourneeInsalubrite:', prime.montantDemiJourneeInsalubrite);
        if (onInsalubriteCat2ConfigChange) {
          console.log('✅ PrimeConfigPanel - Rendering InsalubriteCat2Config');
          return (
            <InsalubriteCat2Config
              config={prime.insalubriteCat2Config || { joursTravaillesParMois: 22 }}
              onChange={onInsalubriteCat2ConfigChange}
              montantDemiJournee={prime.montantDemiJourneeInsalubrite || 0.31}
            />
          );
        } else {
          console.log('❌ PrimeConfigPanel - onInsalubriteCat2ConfigChange is null/undefined for prime:', prime.id);
        }
        return null;

      case 'nbi':
        console.log('🏆 PrimeConfigPanel - nbi case reached');
        console.log('🏆 PrimeConfigPanel - onNBIConfigChange exists:', !!onNBIConfigChange);
        if (onNBIConfigChange) {
          console.log('✅ PrimeConfigPanel - Rendering NBIConfig');
          return (
            <NBIConfig
              config={prime.nbiConfig || { points: 15 }}
              onChange={onNBIConfigChange}
            />
          );
        } else {
          console.log('❌ PrimeConfigPanel - onNBIConfigChange is null/undefined for prime:', prime.id);
        }
        return null;

      default:
        return null;
    }
  };

  const configComponent = renderConfigComponent();

  // Always show config panel if we have a transport base amount handler or other config components
  const shouldShowConfigPanel = configComponent || 
    (prime.id === 'transport' && onTransportBaseAmountChange) ||
    (prime.type === 'variable' && !prime.calculable && onAmountChange) ||
    (prime.type === 'variable' && prime.isMaxCalculated && onAmountChange && prime.id === 'service') ||
    (prime.id === 'insalubrite' && onInsalubriteCat2ConfigChange) ||
    (prime.id === 'iss' && onISSConfigChange) ||
    (prime.id === 'nbi' && onNBIConfigChange);

  if (!shouldShowConfigPanel) {
    return null;
  }

  return (
    <div className="p-3 bg-green-50 rounded-lg border border-green-200">
      <h4 className="text-sm font-medium text-green-800 mb-2 flex items-center gap-1">
        <Icon className="w-4 h-4" />
        Configuration de la prime
      </h4>
      
      {/* Modification du montant pour les primes variables non calculables */}
      {prime.type === 'variable' && !prime.calculable && onAmountChange && prime.id === 'autres' && (
        <div className="mb-4 p-3 bg-white rounded-lg border border-green-300">
          <label className="block text-sm font-medium text-green-800 mb-2">
            Montant mensuel personnalisé
          </label>
          <EditableAmount
            value={prime.montantUtilisateur}
            onSave={(amount) => {
              onAmountChange(amount);
              return true;
            }}
            onCancel={() => {}}
            suffix="€"
            fieldName="montant de la prime autres"
          />
          <div className="text-xs text-green-600 mt-1">
            Modifiez ce montant selon votre situation personnelle
          </div>
        </div>
      )}
      
      {/* Modification du montant pour la prime service avec maximum calculé */}
      {prime.type === 'variable' && prime.isMaxCalculated && onAmountChange && prime.id === 'service' && currentIndiceMajore && (
        <div className="mb-4 p-3 bg-white rounded-lg border border-green-300">
          <label className="block text-sm font-medium text-green-800 mb-2">
            Montant mensuel (maximum : {prime.montantDefaut.toFixed(2)}€)
          </label>
          <EditableAmount
            value={prime.montantUtilisateur}
            onSave={(amount) => {
              if (onAmountChange) {
                onAmountChange(amount);
              }
              return true;
            }}
            onCancel={() => {}}
            suffix="€"
            fieldName="montant de la prime de service"
            max={prime.montantDefaut}
          />
          <div className="text-xs text-green-600 mt-1">
            Maximum calculé : {prime.serviceTauxMax}% du traitement indiciaire brut annuel
          </div>
          <div className="text-xs text-green-700 mt-2 p-2 bg-green-50 rounded">
            <strong>Calcul du maximum :</strong> {currentIndiceMajore} × 59,07€ × {prime.serviceTauxMax}% ÷ 12 = {prime.montantDefaut.toFixed(2)}€
          </div>
        </div>
      )}
      
      {currentIndiceMajore && prime.id !== 'indemnite_residence' && prime.id !== 'transport' && (
        <div className="text-sm text-green-700 mb-3 hidden">
          Indice majoré : <span className="font-medium">{currentIndiceMajore}</span>
        </div>
      )}
      
      {/* Modification du montant de base pour les primes avec remboursement */}
      {prime.id === 'transport' && onTransportBaseAmountChange && (
        <div className="mb-4 p-3 bg-white rounded-lg border border-green-300">
          <label className="block text-sm font-medium text-green-800 mb-2">
            Montant de base mensuel (abonnement transport)
          </label>
          <EditableAmount
            value={prime.montantBase || 40}
            onSave={(amount) => {
              onTransportBaseAmountChange(prime.id, amount);
              return true;
            }}
            onCancel={() => {}}
            suffix="€"
            fieldName="montant de base"
          />
          <div className="text-xs text-green-600 mt-1">
            Montant de votre abonnement transport mensuel
          </div>
          <div className="text-xs text-green-700 mt-2 p-2 bg-green-50 rounded">
            <strong>Calcul :</strong> {prime.montantBase || 40}€ × {prime.pourcentageRemboursement || 75}% = {((prime.montantBase || 40) * (prime.pourcentageRemboursement || 75) / 100).toFixed(2)}€ remboursés
          </div>
        </div>
      )}
      
      {configComponent}
    </div>
  );
}